<div class="space-y-5">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h2 class="text-2xl font-bold text-secondary">Subjects</h2>
      <p class="text-sm text-gray-500 mt-1">
        {{ filteredSubjects.length }} subject{{ filteredSubjects.length === 1 ? '' : 's' }} found
      </p>
    </div>
    <button
      (click)="openAddModal()"
      class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
      Add Subject
    </button>
  </div>

  <!-- Search -->
  <div class="relative">
    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
    <input
      type="text"
      [(ngModel)]="searchQuery"
      (ngModelChange)="onSearch()"
      placeholder="Search by name or code..."
      class="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
    />
  </div>

  <!-- Loader -->
  <app-loader-component [loading]="loading" message="Loading subjects..."></app-loader-component>

  <!-- Error -->
  <div *ngIf="errorMsg && !loading" class="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
    </svg>
    {{ errorMsg }}
  </div>

  <!-- Empty -->
  <div *ngIf="!loading && !errorMsg && filteredSubjects.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
    <svg class="w-10 h-10 text-gray-400 mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"/>
    </svg>
    <p class="text-sm font-medium text-gray-600">No subjects found</p>
    <p class="text-xs text-gray-400 mt-1">Try adjusting your search or add a new subject.</p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && pagedSubjects.length > 0" class="rounded-xl border border-gray-200 overflow-hidden">
    <table class="w-full text-sm">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-200">
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 w-8">#</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Subject</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden sm:table-cell">Code</th>
          <th class="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400 hidden md:table-cell">Theme</th>
          <th class="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        <tr
          *ngFor="let subject of pagedSubjects; let i = index"
          class="bg-white hover:bg-gray-50 transition-colors"
        >
          <!-- Row number -->
          <td class="px-4 py-3 text-gray-400 text-xs">
            {{ page * size + i + 1 }}
          </td>

          <!-- Subject name + icon -->
          <td class="px-4 py-3">
            <div class="flex items-center gap-3">
              <div
                class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                [style.background]="subject.themeColor + '22'"
              >
                <i [class]="'bi bi-' + subject.icon + ' text-base'" [style.color]="subject.themeColor"></i>
              </div>
              <span class="font-semibold text-gray-800">{{ subject.name }}</span>
            </div>
          </td>

          <!-- Code -->
          <td class="px-4 py-3 hidden sm:table-cell">
            <span class="inline-block px-2 py-0.5 rounded-md bg-gray-100 border border-gray-200 text-xs font-mono text-gray-600">
              {{ subject.code }}
            </span>
          </td>

          <!-- Theme colour swatch -->
          <td class="px-4 py-3 hidden md:table-cell">
            <div class="flex items-center gap-2">
              <div class="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0" [style.background]="subject.themeColor"></div>
              <span class="text-xs text-gray-400 font-mono">{{ subject.themeColor }}</span>
            </div>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3">
            <div class="flex items-center justify-end gap-1">

              <!-- Edit -->
              <button
                (click)="openEditModal(subject)"
                title="Edit subject"
                class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary cursor-pointer transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487 18.549 2.8a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z"/>
                </svg>
              </button>

              <!-- Toggle status -->
              <button
                (click)="toggleStatus(subject)"
                [title]="subject.status === 'ACTIVE' ? 'Deactivate' : 'Activate'"
                [disabled]="statusLoadingId === subject.id"
                class="p-1.5 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                [class.text-green-600]="subject.status === 'ACTIVE'"
                [class.text-gray-400]="subject.status !== 'ACTIVE'"
              >
                <svg *ngIf="statusLoadingId !== subject.id" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5.636 5.636a9 9 0 1 0 12.728 0M12 3v9"/>
                </svg>
                <svg *ngIf="statusLoadingId === subject.id" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              </button>

              <!-- Delete -->
              <button
                (click)="openDeleteModal(subject)"
                title="Delete subject"
                class="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer transition-colors hidden"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                </svg>
              </button>

            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="filteredSubjects.length > size" class="flex items-center justify-between pt-2">
    <span class="text-xs text-gray-400">
      Showing {{ getMinValue(page * size + size, filteredSubjects.length) }} of {{ filteredSubjects.length }}
    </span>
    <div class="flex items-center gap-1">
      <button
        (click)="prevPage()"
        [disabled]="!hasPrev"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        Prev
      </button>
      <span class="px-3 py-1.5 text-sm text-gray-600">{{ page + 1 }} / {{ totalPages }}</span>
      <button
        (click)="nextPage()"
        [disabled]="!hasNext"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>

</div>


<!-- ═══════════════════════════════════════════════════════ ADD MODAL -->
<div
  *ngIf="showAddModal"
  class="fixed inset-0 z-50 flex items-center justify-center p-4"
  (click)="closeAddModal()"
>
  <div class="absolute inset-0 bg-black/40"></div>
  <div
    class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5"
    (click)="$event.stopPropagation()"
  >
    <!-- Modal header -->
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Add Subject</h3>
      <button (click)="closeAddModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <!-- Error -->
    <div *ngIf="addError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ addError }}
    </div>

    <!-- Fields -->
    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Name *</label>
        <input
          type="text"
          [(ngModel)]="addForm.name"
          placeholder="e.g. Mathematics"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Code *</label>
        <input
          type="text"
          [(ngModel)]="addForm.code"
          placeholder="e.g. MATHEMATICS"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors font-mono"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Icon (Bootstrap Icons name)</label>
        <input
          type="text"
          [(ngModel)]="addForm.icon"
          placeholder="e.g. calculator"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Theme Colour</label>
        <div class="flex items-center gap-3">
          <input
            type="color"
            [(ngModel)]="addForm.themeColor"
            class="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"
          />
          <span class="text-sm font-mono text-gray-600">{{ addForm.themeColor }}</span>
        </div>
      </div>
    </div>

    <!-- Preview -->
    <div class="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
      <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" [style.background]="addForm.themeColor + '22'">
        <i [class]="'bi bi-' + (addForm.icon || 'book') + ' text-base'" [style.color]="addForm.themeColor"></i>
      </div>
      <div>
        <p class="text-sm font-semibold text-gray-800">{{ addForm.name || 'Subject Name' }}</p>
        <p class="text-xs text-gray-400 font-mono">{{ addForm.code || 'CODE' }}</p>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-end gap-3 pt-1">
      <button
        (click)="closeAddModal()"
        [disabled]="addLoading"
        class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        (click)="submitAdd()"
        [disabled]="addLoading"
        class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg *ngIf="addLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ addLoading ? 'Saving...' : 'Add Subject' }}
      </button>
    </div>
  </div>
</div>


<!-- ═══════════════════════════════════════════════════════ EDIT MODAL -->
<div
  *ngIf="showEditModal"
  class="fixed inset-0 z-50 flex items-center justify-center p-4"
  (click)="closeEditModal()"
>
  <div class="absolute inset-0 bg-black/40"></div>
  <div
    class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5"
    (click)="$event.stopPropagation()"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Edit Subject</h3>
      <button (click)="closeEditModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="editError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ editError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Name *</label>
        <input
          type="text"
          [(ngModel)]="editForm.name"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Code *</label>
        <input
          type="text"
          [(ngModel)]="editForm.code"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors font-mono"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Icon</label>
        <input
          type="text"
          [(ngModel)]="editForm.icon"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
        />
      </div>
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Theme Colour</label>
        <div class="flex items-center gap-3">
          <input type="color" [(ngModel)]="editForm.themeColor" class="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5"/>
          <span class="text-sm font-mono text-gray-600">{{ editForm.themeColor }}</span>
        </div>
      </div>
    </div>

    <!-- Preview -->
    <div class="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
      <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" [style.background]="editForm.themeColor + '22'">
        <i [class]="'bi bi-' + (editForm.icon || 'book') + ' text-base'" [style.color]="editForm.themeColor"></i>
      </div>
      <div>
        <p class="text-sm font-semibold text-gray-800">{{ editForm.name }}</p>
        <p class="text-xs text-gray-400 font-mono">{{ editForm.code }}</p>
      </div>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button
        (click)="closeEditModal()"
        [disabled]="editLoading"
        class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        (click)="submitEdit()"
        [disabled]="editLoading"
        class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg *ngIf="editLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ editLoading ? 'Saving...' : 'Save Changes' }}
      </button>
    </div>
  </div>
</div>


<!-- ═══════════════════════════════════════════════════════ DELETE CONFIRM MODAL -->
<div
  *ngIf="showDeleteModal"
  class="fixed inset-0 z-50 flex items-center justify-center p-4"
  (click)="closeDeleteModal()"
>
  <div class="absolute inset-0 bg-black/40"></div>
  <div
    class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5"
    (click)="$event.stopPropagation()"
  >
    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Delete Subject</h3>
        <p class="text-sm text-gray-600 mt-1">
          Are you sure you want to delete <span class="font-semibold">{{ selectedSubject?.name }}</span>? This action cannot be undone.
        </p>
      </div>
    </div>

    <div *ngIf="deleteError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ deleteError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button
        (click)="closeDeleteModal()"
        [disabled]="deleteLoading"
        class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Cancel
      </button>
      <button
        (click)="confirmDelete()"
        [disabled]="deleteLoading"
        class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg *ngIf="deleteLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ deleteLoading ? 'Deleting...' : 'Delete' }}
      </button>
    </div>
  </div>
</div>